import customerIoLogo from '@/assets/images/logos/logomark-mint.svg';
import { useActiveBundle } from '@/context/active-bundle';
import { Copilot } from '@/features/copilot';
import { GlobalCopilotConfiguration } from '@/features/copilot/global-copilot-configuration';
import { useAuth } from '@/services/auth';
import { FeatureFlag, hasFeatureFlag } from '@/services/auth/feature-flags';
import type { NavigationContextHistoryState } from '@/services/router/navigation-history';
import { useNavigationHistory } from '@/services/router/navigation-history';
import type { TargetTransitionOptions } from '@/services/router/safe-navigate';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { AccountState } from '@/services/auth/types';
import { AppBundle } from '@/utils/app-bundles';
import { shouldProcessLinkClick, useIsSettings } from '@/utils/navigation';
import { Box, Image, Link } from '@customerio/pluma-components/react';
import { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { PersonDropdown } from './components/person-dropdown';
import { SampleWorkspaceBadge } from './components/sample-workspace';
import { WorkspaceSelector } from './components/workspace-selector';
import * as styles from './index.css';
import { NotificationsDropdown } from './components/notifications-dropdown';
import { SupportChat, useSupportChat } from '@/features/support';
import { HelpButton } from '@/features/help-sidebar/help-button';
import { CommandDialogButton } from './components/command-dialog-button';

function ExitSettingsLink() {
  const safeNavigate = useSafeNavigate();
  const activeApp = useActiveBundle();
  const { history } = useNavigationHistory();
  const location = useLocation();

  // Get the latest history entry right as the component is mounted.
  // The navigation context history is updated via an effect, so the current
  // route's info will not be available until the next render.
  // This works for us, because we only need to know the route we were on just
  // before visiting the settings routes anyway.
  const exitSettingsRouteInfo = useRef<NavigationContextHistoryState | null>(null);
  const lastHistoryEntry = history[history.length - 1];

  if (lastHistoryEntry?.url !== `${location.pathname}${location.search}` && exitSettingsRouteInfo.current == null) {
    exitSettingsRouteInfo.current = lastHistoryEntry;
  }

  exitSettingsRouteInfo.current ??= {
    url: '/workspaces/last/journeys',
    app: activeApp,
  };

  const exitSettingsUrl = exitSettingsRouteInfo.current?.url ?? '/';
  const onExitSettingsClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!shouldProcessLinkClick(event)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const targetUrl = event.currentTarget.getAttribute('href') ?? '';
    const containsPipelines = targetUrl.includes(AppBundle.Pipelines);

    safeNavigate(event.currentTarget.getAttribute('href') ?? '', {
      shouldUnrenderOutlet: containsPipelines || activeApp !== exitSettingsRouteInfo.current?.app,
    });
  };

  return (
    <Link
      href={exitSettingsUrl}
      onClick={onExitSettingsClick as any}
      className={styles.exitLink}
      icon="chevron-left"
      variant="secondary"
      ml="200"
    >
      Exit Settings
    </Link>
  );
}

export function TopNavigation() {
  const auth = useAuth();
  const safeNavigate = useSafeNavigate();
  const supportChat = useSupportChat();
  const activeApp = useActiveBundle();

  const { isAnySettingsPage } = useIsSettings();

  const hasWorkspaceAccess =
    auth.account?.account_state !== AccountState.Suspended && auth.account?.account_state !== AccountState.Canceled;

  const showExitSettingsLink =
    auth.account.is_masq ?? false ? !auth.user.noMasqAccess && !auth.user.masqLevelNotSet : hasWorkspaceAccess;

  const logoForNav =
    auth.account?.logo_url !== '' && auth.account?.logo_url != null ? auth.account?.logo_url : customerIoLogo;

  const onLinkClick = (event: React.MouseEvent<HTMLAnchorElement>, opts?: TargetTransitionOptions) => {
    if (!shouldProcessLinkClick(event)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    safeNavigate(event.currentTarget.getAttribute('href') ?? '', opts);
  };

  return (
    <div className={styles.topNav}>
      <Box display="flex" alignItems="center" gap="100">
        {isAnySettingsPage ? (
          showExitSettingsLink ? (
            <ExitSettingsLink />
          ) : null
        ) : (
          <>
            <div className={styles.logoContainer}>
              <Image src={logoForNav} alt="" width="18" />
            </div>
            <Box ml="200">
              <WorkspaceSelector onLinkClick={onLinkClick} />
            </Box>
          </>
        )}

        {Object.values(AppBundle).map((bundle) => (
          <div
            key={bundle}
            className={styles.portal}
            id={`hydra-top-nav-portal-left-${bundle}`}
            style={{ ...(activeApp !== bundle ? { display: 'none' } : {}) }}
          ></div>
        ))}
      </Box>

      <SampleWorkspaceBadge />

      <Box display="flex" alignItems="center" gap="100">
        {Object.values(AppBundle).map((bundle) => (
          <div
            key={bundle}
            className={styles.portal}
            id={`hydra-top-nav-portal-right-${bundle}`}
            style={{ ...(activeApp !== bundle ? { display: 'none' } : {}) }}
          ></div>
        ))}
        {/* Keep the button behind the flag for now */}
        {hasFeatureFlag(FeatureFlag.UniversalSearchDialog) ? <CommandDialogButton /> : null}
        <HelpButton />
        <NotificationsDropdown />
        {hasFeatureFlag(FeatureFlag.AiEnabled) ? (
          <>
            <Copilot />
            <GlobalCopilotConfiguration />
          </>
        ) : supportChat.isHandoverAllowed ? (
          <SupportChat />
        ) : null}
        <PersonDropdown />
      </Box>
    </div>
  );
}
